/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import CopyCartButton from './CopyCartButton'
import type { PageTheme, CartItem } from '@/../types/sale-page'
import { bankIconPath, PROMPTPAY_ICON } from '@/lib/bankIcons'
import { uploadSlipPublic } from '@/lib/salepage/uploadSlip'
import { useAreaShippingQuote } from '@/lib/salepage/useAreaShippingQuote'
import AreaAskModal from './AreaAskModal'
import AreaAskNotice from './AreaAskNotice'

interface AddressResult {
  tambon: string
  amphure: string
  province: string
  zipCode: number
}

interface BankInfo {
  bankCode?: string | null
  bankName?: string | null
  bankAccount?: string | null
  bankAccountName?: string | null
  promptPayId?: string | null
  qrCodeImage?: string | null   // shop's uploaded transfer QR (full URL)
}

interface Props {
  cart: CartItem[]
  cartTotal: number
  shopId: string
  salePageId: string
  pageSlug: string
  allowCOD: boolean
  allowTransfer: boolean
  couponEnabled?: boolean
  bankInfo?: BankInfo
  /** When transfer is selected, which details to show. Account defaults
   *  on, QR (bankInfo.qrCodeImage) defaults off. */
  transferShowAccount?: boolean
  transferShowQr?: boolean
  theme: PageTheme
  /** Color of the final "ยืนยันสั่งซื้อ" submit button. Same value
   *  also drives the "เพิ่มลงตะกร้า" buttons on product cards (set
   *  via Checkout Settings → "สีปุ่มสั่งซื้อ"). Falls back to
   *  theme.primaryColor when unset so existing pages keep their
   *  look without a manual save. */
  orderButtonColor?: string | null
  getImageUrl: (url: string | undefined) => string
  onSuccess: (data: any) => void
  onBack: () => void
  /** Adjust a line's quantity (+1 / −1). When provided, each cart line shows a
   *  −/+ stepper. */
  onUpdateQty?: (productId: string, delta: number) => void
  /** Max total pieces per order (null = unlimited) — caps the + button. */
  maxItems?: number | null
}

/** ข้อความเดียวที่ใช้ทั้งตอนเตือนและตอนล้างคำเตือน — เขียนสองที่แล้วหลุดกันง่าย */
const SLIP_REQUIRED_MSG = 'กรุณาแนบสลิปการโอนเงินก่อนยืนยันคำสั่งซื้อ'

export default function InlineCheckout({
  cart, cartTotal, shopId, salePageId, pageSlug,
  allowCOD, allowTransfer, couponEnabled,
  bankInfo,
  transferShowAccount = true, transferShowQr = false,
  theme, orderButtonColor, getImageUrl,
  onSuccess, onBack, onUpdateQty, maxItems,
}: Props) {
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0)
  const [shippingFeeTransfer, setShippingFeeTransfer] = useState(0)
  const [shippingFeeCOD, setShippingFeeCOD] = useState(0)
  const [freeShippingMin, setFreeShippingMin] = useState<number | null>(null)

  // Fetch shop-wide shipping policy on mount
  useEffect(() => {
    fetch(`/api/public/shipping?shopId=${shopId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setShippingFeeTransfer(data.feeTransfer || 0)
          setShippingFeeCOD(data.feeCOD || 0)
          setFreeShippingMin(data.freeShippingMin || null)
        }
      })
      .catch(() => {})
  }, [shopId])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [couponCode, setCouponCode] = useState('')
  const [couponError, setCouponError] = useState('')
  const [couponChecking, setCouponChecking] = useState(false)
  const [couponResult, setCouponResult] = useState<{ valid: boolean; message: string; discountAmount?: number } | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedField(key)
      setTimeout(() => setCopiedField((k) => (k === key ? null : k)), 1500)
    } catch { /* clipboard blocked — silent */ }
  }

  // Slip — hold in memory, upload on submit
  const [slipFile, setSlipFile] = useState<File | null>(null)
  const [slipPreview, setSlipPreview] = useState<string>('')
  const [slipError, setSlipError] = useState('')
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const selectSlip = (file: File) => {
    setSlipFile(file)
    setSlipPreview(URL.createObjectURL(file))
    setSlipError('')
    // ล้างเฉพาะคำเตือนเรื่องสลิป — error อื่น (เช่นที่อยู่ไม่ครบ) ยังต้องค้างไว้
    setError(prev => (prev === SLIP_REQUIRED_MSG ? '' : prev))
  }

  // Address autocomplete per-field
  const [addressResults, setAddressResults] = useState<AddressResult[]>([])
  const [activeDropdown, setActiveDropdown] = useState<'subdistrict' | 'district' | 'postalCode' | null>(null)

  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    subdistrict: '',
    district: '',
    province: '',
    postalCode: '',
    paymentMethod: (allowCOD ? 'COD' : allowTransfer ? 'TRANSFER' : '') as '' | 'COD' | 'TRANSFER',
    notes: '',
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const markTouched = (field: string) => setTouched(prev => ({ ...prev, [field]: true }))

  // ค่าส่งรายพื้นที่จาก server ตามที่อยู่ที่กรอก (FIXED = ราคาพื้นที่ / ASK = ไม่คิด + popup)
  const areaQuote = useAreaShippingQuote(shopId, form.province, form.district, form.subdistrict)
  const areaAsk = areaQuote?.ask === true
  const [showAskModal, setShowAskModal] = useState(false)

  // Calculate shipping (per-method baseline → ราคาพื้นที่ทับ → FREE_SHIPPING promotion override)
  const baselineShipping = form.paymentMethod === 'COD'
    ? shippingFeeCOD
    : form.paymentMethod === 'TRANSFER'
      ? shippingFeeTransfer
      : Math.max(shippingFeeTransfer, shippingFeeCOD)
  const areaShipping = areaAsk ? 0 : (areaQuote?.fee ?? baselineShipping)
  const actualShipping = (freeShippingMin && cartTotal >= freeShippingMin) ? 0 : areaShipping
  const grandTotal = cartTotal + actualShipping

  // Realtime validation
  const errors: Record<string, string> = {}
  if (touched.name && !form.name.trim()) errors.name = 'กรุณากรอกชื่อ'
  if (touched.phone && !/^0[0-9]{9}$/.test(form.phone)) errors.phone = 'เบอร์โทร 10 หลัก เริ่มด้วย 0'
  if (touched.address && !form.address.trim()) errors.address = 'กรุณากรอกที่อยู่'
  if (touched.subdistrict && !form.subdistrict) errors.subdistrict = 'กรุณากรอกตำบล'
  if (touched.district && !form.district) errors.district = 'กรุณากรอกอำเภอ'
  if (touched.province && !form.province) errors.province = 'กรุณากรอกจังหวัด'

  // Address autocomplete — search when typing in subdistrict / district / postalCode
  useEffect(() => {
    if (!activeDropdown) { setAddressResults([]); return }
    const q = activeDropdown === 'subdistrict' ? form.subdistrict
            : activeDropdown === 'district'    ? form.district
            : form.postalCode
    if (q.length < 2) { setAddressResults([]); return }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/address?action=search&q=${encodeURIComponent(q)}&limit=8`)
        if (res.ok) setAddressResults(await res.json())
      } catch { /* ignore */ }
    }, 300)
    return () => clearTimeout(timer)
  }, [activeDropdown, form.subdistrict, form.district, form.postalCode])

  const selectAddress = (addr: AddressResult) => {
    setForm(f => ({
      ...f,
      subdistrict: addr.tambon,
      district: addr.amphure,
      province: addr.province,
      postalCode: addr.zipCode.toString(),
    }))
    setAddressResults([])
    setActiveDropdown(null)
  }

  const closeDropdown = () => setTimeout(() => setActiveDropdown(null), 150)

  const validateCoupon = async () => {
    if (!couponCode.trim()) return
    setCouponChecking(true)
    setCouponResult(null)
    try {
      const res = await fetch(
        `/api/public/coupon?code=${encodeURIComponent(couponCode)}&salePageId=${salePageId}&total=${grandTotal}`
      )
      const data = await res.json()
      setCouponResult(data)
      if (!data.valid) setCouponError(data.message)
      else setCouponError('')
    } catch {
      setCouponError('ไม่สามารถตรวจสอบโค้ดได้')
    } finally {
      setCouponChecking(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.paymentMethod) { setError('กรุณาเลือกวิธีชำระเงิน'); return }
    // โอนเงิน = ต้องมีสลิป — เดิมแนบหรือไม่ก็ได้ ลูกค้าเลยกดยืนยันทั้งที่ยังไม่โอน
    // แล้วกลับมาแนบทีหลังไม่ได้ (หน้าขายไม่มีเส้นทางนั้น) ⇒ ออเดอร์ค้างไม่ได้เงิน
    // พื้นที่จัดส่งพิเศษ: ยังไม่ให้โอน/แนบสลิป — ต้องเคลียร์ค่าส่งกับแอดมินก่อน
    if (form.paymentMethod === 'TRANSFER' && !slipFile && !areaAsk) {
      setSlipError(SLIP_REQUIRED_MSG)
      setError(SLIP_REQUIRED_MSG)
      fileInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (!form.name || !form.phone || !form.address || !form.subdistrict || !form.district || !form.province) {
      setError('กรุณากรอกข้อมูลจัดส่งให้ครบถ้วน'); return
    }
    // พื้นที่ระบุค่าส่งไม่ได้ — แจ้งลูกค้าก่อน กดรับทราบแล้วค่อยส่งออเดอร์
    if (areaAsk) { setShowAskModal(true); return }
    await submitOrder()
  }

  const submitOrder = async () => {
    setLoading(true)
    setError('')

    try {
      // Upload slip first if provided
      let uploadedSlipUrl: string | undefined
      if (form.paymentMethod === 'TRANSFER' && slipFile) {
        const up = await uploadSlipPublic(slipFile)
        if (up.error || !up.url) {
          setError(up.error || 'อัพโหลดสลิปไม่สำเร็จ กรุณาลองใหม่')
          setLoading(false)
          return
        }
        uploadedSlipUrl = up.url
      }

      const res = await fetch('/api/public/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopId,
          salePageId,
          items: cart.map(i => ({
            productId: i.productId.split('__')[0],
            variantId: i.productId.includes('__') ? i.productId.split('__')[1] : null,
            quantity: i.quantity,
          })),
          customer: {
            name: form.name,
            phone: form.phone,
            address: form.address,
            subdistrict: form.subdistrict,
            district: form.district,
            province: form.province,
            postalCode: form.postalCode,
          },
          paymentMethod: form.paymentMethod,
          couponCode: couponCode || undefined,
          slipUrl: uploadedSlipUrl,
          notes: form.notes,
        }),
      })

      const data = await res.json()
      if (res.ok && data.success) {
        onSuccess(data)
      } else {
        setError(data.error || 'เกิดข้อผิดพลาด')
      }
    } catch {
      setError('ไม่สามารถสร้างคำสั่งซื้อได้')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <AreaAskModal
        open={showAskModal}
        message={areaQuote?.message ?? ''}
        primaryColor={theme.primaryColor}
        onCancel={() => setShowAskModal(false)}
        onConfirm={() => { setShowAskModal(false); void submitOrder() }}
      />
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Cart Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">สรุปคำสั่งซื้อ</h2>
          <div className="space-y-2">
            {cart.map(item => (
              <div key={item.productId} className="flex gap-3 items-center">
                {item.image && <Image src={getImageUrl(item.image)} alt={item.name} width={40} height={40} className="rounded object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{item.name}</p>
                  {onUpdateQty ? (
                    <div className="mt-1 inline-flex items-center gap-2">
                      <button type="button" onClick={() => onUpdateQty(item.productId, -1)} disabled={item.quantity <= 1}
                        className="w-6 h-6 rounded border border-gray-200 text-gray-600 leading-none disabled:opacity-30 hover:bg-gray-50">−</button>
                      <span className="text-sm w-5 text-center">{item.quantity}</span>
                      <button type="button" onClick={() => onUpdateQty(item.productId, 1)} disabled={!!maxItems && cartCount >= maxItems}
                        className="w-6 h-6 rounded border border-gray-200 text-gray-600 leading-none disabled:opacity-30 hover:bg-gray-50">+</button>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">x{item.quantity}</p>
                  )}
                </div>
                <p className="text-sm font-medium">{(item.price * item.quantity).toLocaleString()}฿</p>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 mt-3 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">ค่าสินค้า</span><span>{cartTotal.toLocaleString()}฿</span></div>
            <div className="flex justify-between">
              <span className="text-gray-500">ค่าจัดส่ง</span>
              <span>
                {areaAsk
                  ? <span className="text-amber-600">รอแอดมินยืนยัน</span>
                  : actualShipping === 0 ? <span className="text-green-600">ฟรี</span> : `${actualShipping.toLocaleString()}฿`}
              </span>
            </div>
            {freeShippingMin && cartTotal < freeShippingMin && (
              <p className="text-xs text-blue-600">ซื้อครบ {freeShippingMin.toLocaleString()}฿ ส่งฟรี! (เหลืออีก {(freeShippingMin - cartTotal).toLocaleString()}฿)</p>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-100">
              <span>รวมทั้งหมด</span>
              <span style={{ color: theme.primaryColor }}>{grandTotal.toLocaleString()}฿</span>
            </div>
          </div>
        </div>

        {/* พื้นที่จัดส่งพิเศษ — การ์ดแดงแยกของตัวเอง (ไม่ใช่ข้อความเล็กใต้ค่าส่ง) */}
        {areaAsk && <AreaAskNotice message={areaQuote?.message} transfer={form.paymentMethod === 'TRANSFER'} />}

        {/* Coupon Code */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-800 mb-2">โค้ดส่วนลด</h2>
          <div className="flex gap-2">
            <input
              value={couponCode}
              onChange={e => {
                setCouponCode(e.target.value.toUpperCase())
                setCouponError('')
                setCouponResult(null)
              }}
              onKeyDown={e => e.key === 'Enter' && validateCoupon()}
              placeholder="กรอกโค้ดส่วนลด (ถ้ามี)"
              /* ❌ ห้ามใส่ font-mono ตรงนี้ — ฟอนต์ monospace ของเบราว์เซอร์ไม่มี
                 สระ/พยัญชนะไทย placeholder ภาษาไทยเลยกลายเป็นกล่องสี่เหลี่ยม [?][?]
                 (ตัวโค้ดเป็น A-Z อยู่แล้ว ใช้ tracking-wider พอให้อ่านง่าย) */
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm uppercase tracking-wider focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {couponEnabled && couponCode.trim() && (
              <button
                type="button"
                onClick={validateCoupon}
                disabled={couponChecking}
                className="px-3 py-2 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition flex-shrink-0"
              >
                {couponChecking ? '...' : 'ตรวจสอบ'}
              </button>
            )}
          </div>
          {couponError && <p className="text-xs text-red-600 mt-1.5">{couponError}</p>}
          {couponResult?.valid && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {couponResult.message}
            </div>
          )}
        </div>

        {/* Customer Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 overflow-visible">
          <h2 className="text-sm font-semibold text-gray-800">ข้อมูลจัดส่ง</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">ชื่อ-นามสกุล *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} onBlur={() => markTouched('name')} required className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
              {errors.name && <p className="text-[10px] text-red-500 mt-0.5">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">เบอร์โทร *</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} onBlur={() => markTouched('phone')} required type="tel" className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="08X-XXX-XXXX" />
              {errors.phone && <p className="text-[10px] text-red-500 mt-0.5">{errors.phone}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">ที่อยู่ *</label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} onBlur={() => markTouched('address')} required className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.address ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="บ้านเลขที่ ซอย ถนน" />
            {errors.address && <p className="text-[10px] text-red-500 mt-0.5">{errors.address}</p>}
          </div>

          {/* Address fields with per-field autocomplete */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 overflow-visible">

            {/* ตำบล */}
            <div className="relative">
              <label className="block text-xs text-gray-500 mb-1">ตำบล *</label>
              <input
                value={form.subdistrict}
                onChange={e => { setForm({ ...form, subdistrict: e.target.value }); setActiveDropdown('subdistrict') }}
                onFocus={() => setActiveDropdown('subdistrict')}
                onBlur={() => { markTouched('subdistrict'); closeDropdown() }}
                required
                placeholder="ตำบล"
                autoComplete="off"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.subdistrict ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.subdistrict && <p className="text-[10px] text-red-500 mt-0.5">{errors.subdistrict}</p>}
              {activeDropdown === 'subdistrict' && addressResults.length > 0 && (
                <AddressDropdown results={addressResults} onSelect={selectAddress} />
              )}
            </div>

            {/* อำเภอ */}
            <div className="relative">
              <label className="block text-xs text-gray-500 mb-1">อำเภอ *</label>
              <input
                value={form.district}
                onChange={e => { setForm({ ...form, district: e.target.value }); setActiveDropdown('district') }}
                onFocus={() => setActiveDropdown('district')}
                onBlur={() => { markTouched('district'); closeDropdown() }}
                required
                placeholder="อำเภอ"
                autoComplete="off"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.district ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.district && <p className="text-[10px] text-red-500 mt-0.5">{errors.district}</p>}
              {activeDropdown === 'district' && addressResults.length > 0 && (
                <AddressDropdown results={addressResults} onSelect={selectAddress} />
              )}
            </div>

            {/* จังหวัด — filled automatically, still editable */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">จังหวัด *</label>
              <input
                value={form.province}
                onChange={e => setForm({ ...form, province: e.target.value })}
                onBlur={() => markTouched('province')}
                required
                placeholder="จังหวัด"
                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.province ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {errors.province && <p className="text-[10px] text-red-500 mt-0.5">{errors.province}</p>}
            </div>

            {/* รหัสไปรษณีย์ */}
            <div className="relative">
              <label className="block text-xs text-gray-500 mb-1">รหัสไปรษณีย์</label>
              <input
                value={form.postalCode}
                onChange={e => { setForm({ ...form, postalCode: e.target.value }); setActiveDropdown('postalCode') }}
                onFocus={() => setActiveDropdown('postalCode')}
                onBlur={() => closeDropdown()}
                placeholder="10100"
                maxLength={5}
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {activeDropdown === 'postalCode' && addressResults.length > 0 && (
                <AddressDropdown results={addressResults} onSelect={selectAddress} />
              )}
            </div>

          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800">วิธีชำระเงิน</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {allowCOD && (
              <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${form.paymentMethod === 'COD' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <input type="radio" name="payment" value="COD" checked={form.paymentMethod === 'COD'} onChange={() => setForm({ ...form, paymentMethod: 'COD' })} className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">เก็บเงินปลายทาง</p>
                  <p className="text-[10px] text-gray-500">ชำระเมื่อรับสินค้า</p>
                </div>
              </label>
            )}
            {allowTransfer && (
              <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition ${form.paymentMethod === 'TRANSFER' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <input type="radio" name="payment" value="TRANSFER" checked={form.paymentMethod === 'TRANSFER'} onChange={() => setForm({ ...form, paymentMethod: 'TRANSFER' })} className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">โอนเงิน</p>
                  <p className="text-[10px] text-gray-500">โอนผ่านธนาคาร/พร้อมเพย์</p>
                </div>
              </label>
            )}
          </div>

          {/* Bank info — clean white card with bank logo. */}
          {form.paymentMethod === 'TRANSFER' && !areaAsk && transferShowAccount && bankInfo && (bankInfo.bankAccount || bankInfo.promptPayId) && (() => {
            const bankIcon = bankIconPath(bankInfo.bankCode)
            return (
              <div className="mt-1 rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                <div className="space-y-1.5 text-sm text-slate-800">
                  {bankInfo.bankName && (
                    <div className="flex items-center gap-2">
                      <span className="hidden sm:block text-slate-400 text-xs w-16 flex-shrink-0">ธนาคาร</span>
                      {bankIcon && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={bankIcon} alt="" className="w-5 h-5 rounded-sm object-contain flex-shrink-0" />
                      )}
                      <span className="font-semibold">{bankInfo.bankName}</span>
                    </div>
                  )}
                  {bankInfo.bankAccount && (
                    <div className="flex items-center gap-2">
                      <span className="hidden sm:block text-slate-400 text-xs w-16 flex-shrink-0">เลขบัญชี</span>
                      <span className="font-mono font-semibold tracking-wider">{bankInfo.bankAccount}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(bankInfo.bankAccount!, 'acc')}
                        className={`ml-auto text-[10px] font-semibold rounded px-1.5 py-0.5 transition ${copiedField === 'acc' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        {copiedField === 'acc' ? '✓ คัดลอกแล้ว' : 'คัดลอก'}
                      </button>
                    </div>
                  )}
                  {bankInfo.bankAccountName && (
                    <div className="flex items-center gap-2">
                      <span className="hidden sm:block text-slate-400 text-xs w-16 flex-shrink-0">ชื่อบัญชี</span>
                      <span>{bankInfo.bankAccountName}</span>
                    </div>
                  )}
                  {bankInfo.promptPayId && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-xs w-16 flex-shrink-0">พร้อมเพย์</span>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={PROMPTPAY_ICON} alt="" className="w-5 h-5 rounded-sm object-contain flex-shrink-0" />
                      <span className="font-mono font-semibold">{bankInfo.promptPayId}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(bankInfo.promptPayId!, 'pp')}
                        className={`ml-auto text-[10px] font-semibold rounded px-1.5 py-0.5 transition ${copiedField === 'pp' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      >
                        {copiedField === 'pp' ? '✓ คัดลอกแล้ว' : 'คัดลอก'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Transfer QR — the shop's uploaded QR image (opt-in per page). */}
          {form.paymentMethod === 'TRANSFER' && !areaAsk && transferShowQr && bankInfo?.qrCodeImage && (
            <div className="mt-1 rounded-xl border border-slate-200 bg-white p-3 flex flex-col items-center gap-2">
              <p className="text-xs font-semibold text-slate-600">สแกน QR เพื่อโอนเงิน</p>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={bankInfo.qrCodeImage}
                alt="QR โอนเงิน"
                className="w-48 h-48 object-contain rounded-lg"
              />
            </div>
          )}

          {/* Slip — select file, will upload on submit (พื้นที่พิเศษ: ซ่อน รอแอดมินยืนยันค่าส่งก่อน) */}
          {form.paymentMethod === 'TRANSFER' && !areaAsk && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">แนบสลิปการโอนเงิน <span className="font-semibold text-red-500">*</span> <span className="font-normal text-gray-400">(ต้องแนบก่อนยืนยัน)</span></p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0]
                  if (file) selectSlip(file)
                }}
              />
              {!slipFile ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setIsDragOver(true) }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={e => {
                    e.preventDefault()
                    setIsDragOver(false)
                    const file = e.dataTransfer.files?.[0]
                    if (file) selectSlip(file)
                  }}
                  className={`w-full h-28 flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl cursor-pointer transition select-none
                    ${isDragOver ? 'border-blue-400 bg-blue-50' : slipError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}
                >
                  <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <p className="text-xs text-gray-500">วางไฟล์ที่นี่ หรือ <span className="text-blue-600 font-medium">คลิกเพื่อเลือก</span></p>
                  <p className="text-[10px] text-gray-400">รองรับ JPG, PNG, WebP</p>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 border border-blue-200 bg-blue-50 rounded-xl">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={slipPreview} alt="slip preview" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-blue-200" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-700">เลือกสลิปแล้ว</p>
                    <p className="text-[10px] text-gray-500 truncate">{slipFile.name}</p>
                    <p className="text-[10px] text-blue-500">จะส่งพร้อมคำสั่งซื้อ</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSlipFile(null); setSlipPreview('') }}
                    className="text-gray-400 hover:text-red-500 transition p-1 flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {slipError && <p className="text-xs text-red-600 mt-1">{slipError}</p>}
            </div>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}

        {cart.length > 0 && (
          <CopyCartButton shopId={shopId} salePageId={salePageId} items={cart} className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50" />
        )}

        {/* ปุ่มยังกดได้เสมอเมื่อยังไม่แนบสลิป — กดแล้วพาไปที่ช่องแนบพร้อมข้อความบอกเหตุผล
            ดีกว่าปุ่มเทาที่ไม่บอกอะไร (ลูกค้าบนมือถือจะไม่รู้ว่าติดตรงไหน) */}
        <button
          type="submit"
          disabled={loading || !form.paymentMethod}
          className="w-full py-3.5 text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition text-base"
          style={{ backgroundColor: orderButtonColor || theme.primaryColor }}
        >
          {loading
            ? (form.paymentMethod === 'TRANSFER' && slipFile ? 'กำลังส่งสลิปและสั่งซื้อ...' : 'กำลังสร้างคำสั่งซื้อ...')
            : form.paymentMethod === 'TRANSFER' && !slipFile
              ? 'แนบสลิปการโอนเงินก่อน'
              : `ยืนยันสั่งซื้อ — ${grandTotal.toLocaleString()}฿`}
        </button>
      </form>
    </div>
  )
}

function AddressDropdown({ results, onSelect }: { results: AddressResult[]; onSelect: (a: AddressResult) => void }) {
  return (
    <div className="absolute z-30 left-0 min-w-[280px] mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
      {results.map((addr, i) => (
        <button
          key={i}
          type="button"
          onMouseDown={e => { e.preventDefault(); onSelect(addr) }}
          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 text-xs border-b border-gray-50 last:border-0 transition"
        >
          <span className="font-medium text-gray-800">{addr.tambon}</span>
          <span className="text-gray-400 mx-1">›</span>
          <span className="text-gray-600">{addr.amphure}</span>
          <span className="text-gray-400 mx-1">›</span>
          <span className="text-gray-600">{addr.province}</span>
          <span className="ml-2 font-mono text-blue-600">{addr.zipCode}</span>
        </button>
      ))}
    </div>
  )
}
